import {chaveDoRecurso} from "./catalogoRegras";

// Regras da página única do sistema (BBF-174, protótipo v2 validado em 18/10/2026). As pendências vêm da
// API (`GET /sistemas/{id}/pendencias`, BBF-173); aqui ficam só o agrupamento, a completude de cada seção,
// a memória do que estava aberto e a montagem do payload dos endpoints.

export const STATUS_SISTEMA = {
    VENDA: {rotulo: 'À venda', cor: 'success'},
    DESENVOLVIMENTO: {rotulo: 'Em desenvolvimento', cor: 'warning'},
    INTERNO: {rotulo: 'Uso interno', cor: 'default'},
};

export const SECOES = [
    {id: 'pendencias', rotulo: 'Pendências'},
    {id: 'dados', rotulo: 'Dados do sistema'},
    {id: 'catalogo', rotulo: 'Módulos e funcionalidades'},
    {id: 'metricas', rotulo: 'Métricas'},
    {id: 'fiscal', rotulo: 'Fiscal'},
    {id: 'integracao', rotulo: 'Integração'},
];

// Os endereços das abas antigas da BBF-143 levam à seção equivalente.
const SECAO_ANTIGA = {'visao-geral': 'dados', modulos: 'catalogo', recursos: 'catalogo'};

export const secaoDoEndereco = (secao) => {
    const valor = SECAO_ANTIGA[secao] || secao;
    return SECOES.some((item) => item.id === valor) ? valor : null;
};

export const EVENTOS_ASSINAVEIS = [
    'assinatura.ativada',
    'assinatura.suspensa',
    'assinatura.reativada',
    'assinatura.cancelada',
    'direito.alterado',
];

export const GRAUS = [
    {id: 'bloqueia', titulo: 'Bloqueiam o uso', cor: 'error'},
    {id: 'construcao', titulo: 'Em construção: não liberam nada', cor: 'warning'},
    {id: 'aviso', titulo: 'Avisos', cor: 'info'},
];

export const METODOS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

export const listaCurta = (nomes, maximo = 4) => (
    nomes.slice(0, maximo).join(', ') + (nomes.length > maximo ? ` e mais ${nomes.length - maximo}` : '')
);

const itensDe = (pendencias) => pendencias?.itens || [];
const doGrau = (itens, grau) => itens.filter((item) => item.grau === grau);

export const pendenciasPorGrau = (pendencias) => {
    const itens = itensDe(pendencias);
    return {
        bloqueia: doGrau(itens, 'bloqueia'),
        construcao: doGrau(itens, 'construcao'),
        aviso: doGrau(itens, 'aviso'),
    };
};

// Pendências que travam o módulo: as que bloqueiam e apontam para ele.
export const bloqueiosDoModulo = (pendencias, moduloId) => itensDe(pendencias).filter((item) => (
    item.grau === 'bloqueia' && item.alvo?.tipo === 'modulo' && item.alvo.id === moduloId
));

export const emConstrucaoNoModulo = (pendencias, moduloId) => itensDe(pendencias).filter((item) => (
    item.grau === 'construcao' && item.alvo?.tipo === 'funcionalidade' && item.alvo.modulo_id === moduloId
));

// Completude de cada seção, para a navegação lateral e o cabeçalho de cada cartão.
export const completude = (secao, {sistema, pendencias}) => {
    const itens = itensDe(pendencias);
    if (secao === 'pendencias') {
        const b = doGrau(itens, 'bloqueia').length;
        const c = doGrau(itens, 'construcao').length;
        const a = doGrau(itens, 'aviso').length;
        if (!pendencias) return {cor: 'default', texto: 'Carregando'};
        if (b) return {cor: 'error', texto: `${plural(b, 'bloqueia', 'bloqueiam')} o uso${c ? ` · ${c} em construção` : ''}`};
        if (c) return {cor: 'warning', texto: `${c} em construção`};
        if (a) return {cor: 'info', texto: plural(a, 'aviso', 'avisos')};
        return {cor: 'success', texto: 'Nada pendente'};
    }
    if (secao === 'fiscal') return {cor: 'default', texto: 'Ainda sem API'};
    if (secao === 'integracao' && sistema?.status === 'INTERNO') return {cor: 'default', texto: 'Não se aplica ao uso interno'};
    const daSecao = itens.filter((item) => item.secao === secao);
    const b = doGrau(daSecao, 'bloqueia').length;
    const c = doGrau(daSecao, 'construcao').length;
    const a = doGrau(daSecao, 'aviso').length;
    if (b) return {cor: 'error', texto: `${plural(b, 'pendência', 'pendências')}${c ? ` · ${c} em construção` : ''}`};
    if (c) return {cor: 'warning', texto: `${c} em construção`};
    if (a) return {cor: 'info', texto: plural(a, 'aviso', 'avisos')};
    if (secao === 'metricas' && !(sistema?.metricas || []).length) return {cor: 'default', texto: 'Nenhuma apontada'};
    return {cor: 'success', texto: 'Completa'};
};

// Onde a pendência se resolve: a seção e, no catálogo, o módulo e a funcionalidade que abrem.
export const alvoDaPendencia = (item) => {
    const alvo = item.alvo || {};
    if (alvo.tipo === 'funcionalidade') return {secao: item.secao, moduloId: alvo.modulo_id, funcionalidadeId: alvo.id};
    if (alvo.tipo === 'modulo') return {secao: item.secao, moduloId: alvo.id};
    return {secao: item.secao};
};

export const idDaSecao = (secao) => `sec-${secao}`;
export const idDoModulo = (id) => `md-${id}`;
export const idDaFuncionalidade = (id) => `fn-${id}`;

/* ---------- memória da página: o que estava aberto e onde a pessoa parou ---------- */

const chaveDaMemoria = (sistemaId) => `busifarm.sistema-pagina.${sistemaId}`;

export const memoriaVazia = () => ({abertos: {}, funcs: {}, ultimo: null});

export const lerMemoria = (sistemaId) => {
    try {
        const valor = JSON.parse(localStorage.getItem(chaveDaMemoria(sistemaId)));
        return valor ? {...memoriaVazia(), ...valor} : null;
    } catch {
        return null;
    }
};

export const gravarMemoria = (sistemaId, memoria) => {
    try {
        localStorage.setItem(chaveDaMemoria(sistemaId), JSON.stringify(memoria));
    } catch {
        // Sem o armazenamento do navegador, a página só não lembra o que estava aberto.
    }
};

/* ---------- fonte do catálogo (BBF-103) ---------- */

// Catálogo de manifesto: a estrutura (módulos, funcionalidades, tipo e endpoints) vem de um arquivo no
// repositório do sistema, como o catalogo.yml do BusiPet, e é somente leitura aqui. A API recusa a escrita.
export const catalogoDeManifesto = (sistema) => sistema?.fonte_catalogo?.tipo === 'MANIFESTO';

export const revisaoCurta = (revisao) => (revisao ? revisao.slice(0, 7) : '');

export const dataDaAplicacao = (aplicadoEm) => {
    const data = aplicadoEm ? new Date(aplicadoEm) : null;
    return data && !Number.isNaN(data.getTime()) ? data.toLocaleDateString('pt-BR') : '';
};

// No catálogo de manifesto, a pendência do catálogo se corrige no manifesto (a API manda a origem em `manifesto`).
export const correcaoNoManifesto = (item) => {
    if (!item?.manifesto) return '';
    return item.grau === 'aviso' ? 'Se precisar mudar, altere o manifesto' : 'Corrija no manifesto';
};

/* ---------- catálogo ---------- */

export const funcionalidadeCompleta = (funcionalidade) => Boolean(funcionalidade.recursos?.length);

export const contagem = (modulos) => {
    const funcionalidades = modulos.flatMap((modulo) => modulo.funcionalidades || []);
    const completas = funcionalidades.filter(funcionalidadeCompleta);
    const leitura = completas.filter((item) => item.tipo === 'LEITURA').length;
    return {
        funcionalidades: funcionalidades.length,
        completas: completas.length,
        leitura,
        escrita: completas.length - leitura,
        endpoints: funcionalidades.reduce((total, item) => total + (item.recursos?.length || 0), 0),
    };
};

export const validarEndpoint = (metodo, caminho, recursosAtuais = []) => {
    const valor = (caminho || '').trim();
    if (!METODOS.includes(metodo)) return 'Escolha o método.';
    if (!/^\/\S*$/.test(valor)) return 'O caminho começa com / e não tem espaços.';
    const chave = chaveDoRecurso({metodo, endpoint: valor});
    if (recursosAtuais.some((recurso) => chaveDoRecurso(recurso) === chave)) return `${metodo} ${valor} já está nesta funcionalidade.`;
    return '';
};

// O PUT da funcionalidade sincroniza a lista: quem vai com o `id` mantém as permissões, quem fica de fora sai.
// O recurso novo leva o próprio endpoint como nome, porque a tela pede só o método e o caminho.
export const recursosParaPayload = (recursos = []) => recursos.map((recurso) => ({
    ...(recurso.id ? {id: recurso.id} : {}),
    nome: recurso.nome || `${recurso.metodo} ${recurso.endpoint}`,
    endpoint: recurso.endpoint,
    metodo: recurso.metodo,
}));

export const novoRecurso = (metodo, caminho) => ({metodo, endpoint: caminho.trim(), nome: `${metodo} ${caminho.trim()}`});

/* ---------- integração ---------- */

const NOME_DA_FONTE = {EVENTOS: 'eventos', FOTO: 'foto diária'};

// Os mesmos itens de `faltas_da_integracao` da API. O envio de uso só é exigido quando alguma métrica apontada
// vem do sistema (eventos ou foto diária); a que o backoffice mede sozinho, pelos vínculos, não precisa dele.
export const itensDaIntegracao = (integracao = {}, metricas = []) => {
    const eventos = integracao.eventos_assinados || [];
    const faltam = EVENTOS_ASSINAVEIS.filter((evento) => !eventos.includes(evento));
    const precisamDoEnvio = metricas.filter((metrica) => metrica.fonte !== 'BACKOFFICE');
    return [
        {
            id: 'url',
            ok: Boolean(integracao.url_provisionamento),
            titulo: 'URL de provisionamento',
            texto: 'É para onde o backoffice manda os eventos da assinatura.',
        },
        {
            id: 'eventos',
            ok: !faltam.length,
            titulo: 'Todos os eventos assinados',
            texto: `${eventos.length} de ${EVENTOS_ASSINAVEIS.length}.${eventos.includes('assinatura.suspensa') && eventos.includes('direito.alterado') ? '' : ' Sem assinatura.suspensa e direito.alterado, o sistema não fica sabendo de suspensão, do modo somente consulta nem de opcional contratado.'}`,
        },
        {
            id: 'envio',
            ok: !precisamDoEnvio.length || Boolean(integracao.canal_envio_uso),
            titulo: 'Envio de uso',
            texto: precisamDoEnvio.length
                ? `Necessário: ${precisamDoEnvio.map((metrica) => `${metrica.nome} (${NOME_DA_FONTE[metrica.fonte] || metrica.fonte})`).join(', ')}.`
                : 'Nenhuma métrica depende do sistema.',
        },
        {
            id: 'segredo',
            ok: Boolean(integracao.segredo_ref),
            titulo: 'Referência do segredo',
            texto: 'A assinatura dos eventos usa esse segredo.',
        },
    ];
};

/* ---------- prévia do efeito de mover ou remover ---------- */

export const nomeDaAssinatura = (assinatura) => {
    const empresa = assinatura.empresa?.nome || assinatura.tenant_id || assinatura.id;
    return assinatura.plano?.nome ? `${empresa} (${assinatura.plano.nome})` : empresa;
};

export const nomeDoPerfil = (perfil, assinaturas = []) => {
    const assinatura = assinaturas.find((item) => item.id === perfil.licenca_id);
    return assinatura ? `${perfil.nome} (${assinatura.empresa?.nome || assinatura.tenant_id})` : perfil.nome;
};
